import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '@/components/PageTitle';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toaster';
import { errorText } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { useCan } from '@/lib/session';
import { CrmStatusBadge } from '@/components/CrmStatusBadge';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';

export const Route = createFileRoute('/_app/leads/$leadId')({
  beforeLoad: ({ context }) => requirePermission(context.me, 'leads.view'),
  component: LeadPage
});

function LeadPage() {
  const { t } = useTranslation();
  const { leadId } = Route.useParams();
  const can = useCan();
  const lead = useQuery(orpc.leads.get.queryOptions({ input: { id: leadId } }));
  const l = lead.data;
  if (lead.isError) return <div className="p-6">{t('errors.NOT_FOUND')}</div>;
  if (!l) return <div className="skeleton m-6 h-64" />;
  const name = [l.firstName, l.lastName].filter(Boolean).join(' ') || '–';
  const facts: [string, string | null][] = [
    [t('leads.email'), l.email],
    [t('leads.phone'), l.phone],
    [t('leads.company'), l.company],
    [t('leads.jobTitle'), l.jobTitle],
    [t('leads.country'), l.country],
    [t('leads.campaign'), l.campaignName],
    [t('leads.form'), l.formName],
    [t('leads.submitted'), formatDateTime(l.submittedAt)],
    [t('leads.firstSeen'), formatDateTime(l.firstSeenAt)]
  ];

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={name}
        description={l.company}
        items={[{ label: t('leads.title'), path: '/leads' }, { label: name, active: true }]}
        actions={can('leads.erase') && !l.erased && <EraseButton leadId={l.id} hasEmail={l.email !== null} />}
      />
      <div className="flex flex-wrap gap-2">
        {l.testLead && <span className="badge badge-ghost">{t('leads.test')}</span>}
        {l.withdrawn && <span className="badge badge-warning badge-soft">{t('leads.withdrawn')}</span>}
        {l.erased && <span className="badge badge-error badge-soft">{t('leads.erased')}</span>}
        {!l.piiVisible && <span className="badge badge-info badge-soft">{t('leads.masked')}</span>}
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-base">{t('leads.details')}</h2>
            <dl className="mt-2 grid grid-cols-[10rem_1fr] gap-y-2 text-sm">
              {facts.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-base-content/60">{k}</dt>
                  <dd>{v ?? '–'}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-base">{t('leads.answers')}</h2>
            <dl className="mt-2 space-y-2 text-sm">
              {l.answers.map((a) => (
                <div key={a.questionId}>
                  <dt className="text-base-content/60">{a.label ?? a.name ?? `#${a.questionId}`}</dt>
                  <dd>{a.value}</dd>
                </div>
              ))}
            </dl>
            {l.consents.length > 0 && (
              <>
                <h3 className="mt-4 font-medium">{t('leads.consents')}</h3>
                <ul className="mt-1 text-sm">
                  {l.consents.map((c) => (
                    <li key={c.consentId} className="flex items-center gap-2">
                      <span className={`iconify size-4 ${c.accepted ? 'lucide--check text-success' : 'lucide--x text-error'}`} />#{c.consentId}{' '}
                      {c.accepted ? t('leads.consentGiven') : t('leads.consentDenied')}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
      {can('crm.view') && <CrmCard leadId={leadId} />}
    </div>
  );
}

function EraseButton({ leadId, hasEmail }: { leadId: string; hasEmail: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sameEmail, setSameEmail] = useState(true);
  const [crmIds, setCrmIds] = useState<string[] | null>(null);
  const erase = useMutation({
    mutationFn: () => client.leads.erase({ id: leadId, sameEmail: hasEmail && sameEmail }),
    onSuccess: async (r) => {
      toast('success', t('leads.erasedDone', { count: r.erased.length }));
      if (r.crmLeadIds.length) setCrmIds(r.crmLeadIds);
      else setOpen(false);
      await queryClient.invalidateQueries({ queryKey: orpc.leads.key() });
      await queryClient.invalidateQueries({ queryKey: orpc.crm.key() });
    },
    onError: (e) => toast('error', errorText(t, e))
  });
  return (
    <>
      <button type="button" className="btn btn-sm btn-ghost text-error" onClick={() => setOpen(true)}>
        <span className="iconify lucide--user-x size-4" />
        {t('leads.erase')}
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('leads.eraseTitle')}
        actions={
          crmIds ? (
            <button type="button" className="btn btn-sm" onClick={() => setOpen(false)}>
              {t('common.close')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>
                {t('admin.cancel')}
              </button>
              <button type="button" className="btn btn-sm btn-error" disabled={erase.isPending} onClick={() => erase.mutate()}>
                {t('leads.eraseConfirm')}
              </button>
            </>
          )
        }
      >
        {crmIds ? (
          <div className="space-y-2 text-sm">
            <p>{t('leads.eraseCrmNote', { count: crmIds.length })}</p>
            <ul className="font-mono text-xs">
              {crmIds.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p>{t('leads.eraseText')}</p>
            {hasEmail && (
              <label className="flex cursor-pointer items-center gap-2">
                <input type="checkbox" className="checkbox checkbox-sm" checked={sameEmail} onChange={(e) => setSameEmail(e.target.checked)} />
                {t('leads.eraseSameEmail')}
              </label>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

function CrmCard({ leadId }: { leadId: string }) {
  const { t } = useTranslation();
  const can = useCan();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const status = useQuery(orpc.crm.status.queryOptions());
  const sync = useQuery({ ...orpc.crm.forLead.queryOptions({ input: { leadId } }), enabled: status.data?.configured === true });
  const onError = (e: unknown) => toast('error', errorText(t, e));
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.crm.key() });
  const retry = useMutation({
    mutationFn: (id: string) => client.crm.retry({ id }),
    onSuccess: async () => {
      toast('success', t('crm.retried'));
      await refresh();
    },
    onError
  });
  const release = useMutation({
    mutationFn: (id: string) => client.crm.release({ ids: [id] }),
    onSuccess: async (r) => {
      toast('success', t('crm.released', { count: r.released }));
      await refresh();
    },
    onError
  });
  const build = useMutation({ mutationFn: () => client.crm.preview({ leadId }), onSuccess: (r) => setPreview(r.payload), onError });
  if (!status.data?.configured || sync.isPending) return null;
  const s = sync.data;
  const actions = (
    <div className="flex flex-wrap gap-2">
      {s?.status === 'failed' && can('crm.sync.retry') && (
        <button type="button" className="btn btn-xs btn-outline border-base-300" disabled={retry.isPending} onClick={() => retry.mutate(s.id)}>
          {t('crm.retry')}
        </button>
      )}
      {s?.status === 'held' && can('crm.sync.release') && (
        <button type="button" className="btn btn-xs btn-outline border-base-300" disabled={release.isPending} onClick={() => release.mutate(s.id)}>
          {t('crm.release')}
        </button>
      )}
      {can('crm.mapping.manage') && can('leads.view_pii') && (
        <button type="button" className="btn btn-xs btn-ghost" disabled={build.isPending} onClick={() => build.mutate()}>
          {t('crm.preview')}
        </button>
      )}
    </div>
  );
  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="card-title text-base">{t('crm.leadCard')}</h2>
          {actions}
        </div>
        {!s ? (
          <p className="text-base-content/60 text-sm">{t('crm.notQueued')}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <CrmStatusBadge item={s} />
            {s.matchResult && <span className="text-base-content/70">{t(`crm.match.${s.matchResult}` as never, { defaultValue: s.matchResult })}</span>}
            {s.sentAt && (
              <span>
                {t('crm.sentAt')}: {formatDateTime(s.sentAt)}
              </span>
            )}
            {s.lastError && s.status === 'failed' && <span className="text-error">{s.lastError}</span>}
            {s.crmUrl && (
              <a href={s.crmUrl} target="_blank" rel="noreferrer" className="link">
                {t('crm.openInCrm')}
              </a>
            )}
          </div>
        )}
        {preview && <pre className="bg-base-200 mt-2 overflow-x-auto rounded p-3 text-xs">{JSON.stringify(preview, null, 2)}</pre>}
      </div>
    </div>
  );
}
